// wagmi never participates in auth: a connected wallet is a
// transaction-side bystander the auth layer ignores.

import {
  use,
  useCallback,
  useEffect,
  useState,
  useRef,
  createContext,
  type ReactNode,
} from 'react';
import { useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { track } from '@/shared/utils/track';
import posthog from '@/shared/utils/posthog';
import { captureException } from '@/modules/capture-exception';
import { StoredAuth, StoredInviteCode } from '@/shared/utils/Stored';
import { clearInviteCodeSignal } from '@/modules/invite-code-signal';
import { emitInviteRequired } from '@/modules/invite-required-signal';
import { showNotification } from '@/modules/show-notification';
import {
  fetchApiRaw,
  setOnSessionExpired,
  setSessionRefresher,
  accessTokenAtom,
} from '@/modules/sanr-api';
import { FetchApiError } from '@/modules/fetch-core';
import {
  resolveSsoConfig,
  prepareSsoRedirect,
  ssoRedirectUri,
  leaveForSso,
} from '@/shared/utils/sso';
import type { Profile } from '@/modules/sanr-api';
import {
  clearStoredSession,
  runSsoLogin,
  runSsoSwitch,
  runSsoSwitchCallback,
  trackLogout,
  fetchProfileWithLiveToken,
} from './handlers';
import useRestoreInitialSession from './useRestoreInitialSession';
import AuthOverlay from './AuthOverlay';

export type { Profile };

type ProfileContextValue = {
  profile: Profile | null;
  isAuthReady: boolean;
  // While the boot restore or a login leg is in flight a sign-in affordance
  // either renders inert or defers the click until ready, so a restore
  // landing a session mid-wait enters the app instead of starting a
  // redirect over it.
  isAuthPending: boolean;
  updateProfile: () => Promise<Profile>;
  login: () => void;
  logout: () => void;
  // Local teardown alone — no server logout, no SSO end-session redirect.
  // For the demo-stand reset, where the SSO side has already revoked the
  // previous session and the page is about to leave for a fresh login.
  resetLocalSession: () => void;
  completeSsoLogin: (args: {
    code: string;
    codeVerifier: string;
  }) => Promise<'success' | 'invite_required' | 'error'>;
  // 'redirect' = the browser is leaving for the silent-auth round trip and
  // the /auth/callback route finishes via completeSsoSwitch; the failure
  // outcomes keep the current session.
  switchSsoAccount: (
    targetSub: string,
  ) => Promise<'success' | 'redirect' | 'invite_required' | 'error'>;
  // returnTo is the server-echoed same-origin path to land on after a
  // successful switch.
  completeSsoSwitch: (args: { state: string; code: string }) => Promise<{
    outcome: 'success' | 'invite_required' | 'error';
    returnTo: string | null;
  }>;
};

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function useProfile(): ProfileContextValue {
  const ctx = use(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within ProfileProvider');
  return ctx;
}

export default function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [overlayText, setOverlayText] = useState<string | null>(null);
  // Monotonic guard for profile writes: an in-flight GET_ISSUER may only
  // setProfile if nothing newer wrote in between. Bumped by updateProfile
  // itself AND by logout/login (otherwise a stale response resolving after
  // a logout resurrected the cleared profile).
  const profileFetchSeqRef = useRef(0);

  const queryClient = useQueryClient();
  const location = useLocation();
  const setAccessToken = useSetAtom(accessTokenAtom);

  // Sync token for non-React fetchApi callers.
  useEffect(() => {
    setAccessToken(profile?.accessToken);
  }, [profile?.accessToken, setAccessToken]);

  // eslint-disable-next-line no-restricted-syntax -- resetLocalSession, built from it in the context value, is an effect dependency in DemoReset
  const endSession = useCallback(
    (reason: 'user' | 'session_expired') => {
      const hadSession = Boolean(StoredAuth.get() ?? profile);
      // Invalidate any in-flight GET_ISSUER — its late response must not
      // resurrect the profile after this clear.
      profileFetchSeqRef.current++;
      clearStoredSession(queryClient);
      setProfile(null);
      if (hadSession) {
        // A captured ?invite_code= must not outlive a DELIBERATE logout —
        // the next uninvited login on this browser would silently consume
        // the one-time code. Only reason 'user' clears it: session_expired
        // is the same person re-authenticating and needs the code to
        // survive into the next login. Failed logins (hadSession false)
        // keep it too, for the deliberate network-error retry.
        if (reason === 'user') {
          StoredInviteCode.clear();
          // The module-scope signal buffer too — it survives client-side
          // navigation and would re-fire the invite modal on the landing.
          clearInviteCodeSignal();
        }
        trackLogout(reason);
      }
    },
    [profile, queryClient],
  );

  useEffect(() => {
    setOnSessionExpired(() => {
      endSession('session_expired');
    });
  });

  // fetchApiRaw's expired-token recovery: null = no session to refresh; a
  // rejected refresh propagates and fetchApiRaw falls through to the expiry
  // teardown.
  // eslint-disable-next-line no-restricted-syntax -- effect dependency in the setSessionRefresher effect below
  const refreshSession = useCallback(async () => {
    const stored = StoredAuth.get();
    if (!stored) return null;
    const { accessToken } = await fetchApiRaw('POST_AUTH_REFRESH', {
      signal: undefined,
      body: { refreshToken: stored.refreshToken },
    });
    // The session may have been replaced (an account switch, a login
    // completing in another tab) or ended while the mint was in flight —
    // committing the pre-await snapshot then would resurrect the OLD
    // identity's row and token over the new session. The caller's retry
    // still gets the token its own session minted (the old session stays
    // live server-side), but nothing is persisted for it.
    if (StoredAuth.get()?.refreshToken !== stored.refreshToken) {
      return accessToken;
    }
    StoredAuth.set({ ...stored, accessToken });
    setAccessToken(accessToken);
    setProfile((p) => (p ? { ...p, accessToken } : p));
    return accessToken;
  }, [setAccessToken]);

  useEffect(() => {
    setSessionRefresher(refreshSession);
  }, [refreshSession]);

  useRestoreInitialSession({ setProfile, setIsAuthReady });

  // Concurrent updateProfile calls (a dropdown-open refresh racing a
  // tutorial-mark refresh, the Settings polls) can resolve out of order —
  // only the newest request may write state, or an older GET_ISSUER
  // response landing last would transiently revert a fresher profile
  // (e.g. flicker a just-latched tutorial flag off).
  // eslint-disable-next-line no-restricted-syntax -- effect dependency in the focus-refetch effect below and in ProfileMenu's and AccountsCard's effects
  const updateProfile = useCallback(async () => {
    const stored = StoredAuth.get();
    if (!stored) throw new Error('updateProfile called without session');
    const seq = ++profileFetchSeqRef.current;
    const updated = await fetchProfileWithLiveToken(stored.accessToken);
    if (seq === profileFetchSeqRef.current) {
      setProfile(updated);
      posthog.setPersonProperties({ username: updated.username });
    }
    return updated;
  }, []);

  // The profile is plain React state, not a query, so nothing refreshes it
  // passively: issuer changes made from another tab/device (watchlist,
  // telegram link, tutorial latches) would stay invisible until an explicit
  // updateProfile. Throttled like react-query's default focus refetch.
  const lastFocusRefreshRef = useRef(0);
  const hasProfile = profile !== null;
  useEffect(() => {
    if (!hasProfile) return;
    const onFocus = () => {
      if (Date.now() - lastFocusRefreshRef.current < 60_000) return;
      if (!StoredAuth.get()) return;
      lastFocusRefreshRef.current = Date.now();
      updateProfile().catch(() => {
        // Transient failure — the next focus past the throttle retries.
      });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [hasProfile, updateProfile]);

  const login = () => {
    if (profile) return;
    // resolveSsoConfig AWAITS an in-flight discovery — reading the hook's
    // snapshot here would let the first click after a page load race the
    // fetch into the unavailable branch.
    void (async () => {
      const config = await resolveSsoConfig(queryClient);
      if (config?.enabled === true) {
        track('sso_login_started', { method: 'redirect' });
        leaveForSso(
          await prepareSsoRedirect(config, { prompt: 'select_account' }),
        );
        return;
      }
      // SSO is the only login — with discovery unavailable (endpoint down
      // or reporting SSO disabled) there is nothing to offer but the
      // failure.
      showNotification('error', 'Sign-in is unavailable right now');
    })();
  };

  const logout = () => {
    const stored = StoredAuth.get();
    // Guide order — server logout, local teardown, end-session redirect.
    // The server call runs while the bearer still exists (an expired
    // token refreshes transparently); its failure must never block the
    // local logout, only skip the redirect (the SSO cookie then
    // survives and the next Santiment login silently resumes the same
    // account).
    void (async () => {
      let ssoLogoutUrl: string | null = null;
      if (stored) {
        try {
          ({ ssoLogoutUrl } = await fetchApiRaw(
            'POST_AUTH_LOGOUT',
            { signal: undefined },
            stored.accessToken,
          ));
        } catch (e) {
          // Logout is non-idempotent by contract — a 401 means the session
          // was already ended elsewhere. That is the outcome the user
          // asked for, not a fault to page on.
          if (!(e instanceof FetchApiError && e.extra.status === 401)) {
            captureException(e);
          }
        }
      }
      endSession('user');
      if (ssoLogoutUrl != null) leaveForSso(ssoLogoutUrl);
    })();
  };

  // eslint-disable-next-line no-restricted-syntax -- effect dependency in AuthCallback's return-leg effect
  const completeSsoLogin = useCallback(
    (args: { code: string; codeVerifier: string }) =>
      runSsoLogin({
        ...args,
        redirectUri: ssoRedirectUri(),
        queryClient,
        setProfile: (p) => {
          profileFetchSeqRef.current++;
          setProfile(p);
        },
        setOverlayText,
        onInviteRequired: emitInviteRequired,
      }),
    [queryClient],
  );

  const switchSsoAccount = (targetSub: string) =>
    runSsoSwitch({
      targetSub,
      redirectUri: ssoRedirectUri(),
      // Land back where the switch started; the server enforces same-origin
      // and the value only matters on the redirect leg.
      returnTo: `${window.location.pathname}${window.location.search}`,
      queryClient,
      setProfile: (p) => {
        profileFetchSeqRef.current++;
        setProfile(p);
      },
      setOverlayText,
    });

  // eslint-disable-next-line no-restricted-syntax -- effect dependency in AuthCallback's return-leg effect
  const completeSsoSwitch = useCallback(
    (args: { state: string; code: string }) =>
      runSsoSwitchCallback({
        ...args,
        queryClient,
        setProfile: (p) => {
          profileFetchSeqRef.current++;
          setProfile(p);
        },
        setOverlayText,
      }),
    [queryClient],
  );

  const isAuthPending = !isAuthReady || overlayText !== null;
  // The landing keeps the boot restore off-screen (its auth cluster waits
  // for the settle itself); every other route shows the overlay.
  const showOverlay = location.pathname !== '/' && isAuthPending;

  const value: ProfileContextValue = {
    profile,
    isAuthReady,
    isAuthPending,
    updateProfile,
    login,
    logout,
    resetLocalSession: () => endSession('user'),
    completeSsoLogin,
    switchSsoAccount,
    completeSsoSwitch,
  };

  return (
    <ProfileContext value={value}>
      {children}
      {showOverlay && <AuthOverlay text={overlayText} />}
    </ProfileContext>
  );
}
